<template>
  <div class="optics-lab-viz">
    
    <div class="lab-toolbar">
      <div class="brand">光学实验室 v2.0</div>
      <div class="tag-group">
         <span class="chip">折射系数 n2={{ n2.toFixed(2) }}</span>
         <span class="chip yellow">全反射监测: {{ isTIR ? '已发生' : '未发生' }}</span>
      </div>
    </div>

    <div class="viz-row">
      <div class="optics-bench interactive" @mousemove="handleMouseMove">
        <svg width="100%" height="100%" viewBox="0 0 500 400" preserveAspectRatio="xMidYMid meet">
           <!-- 渐变背景 (模拟实验室暗室) -->
           <defs>
              <linearGradient id="lab-bg" x1="0" y1="0" x2="0" y2="1">
                 <stop offset="0%" stop-color="#0f172a" />
                 <stop offset="100%" stop-color="#020617" />
              </linearGradient>
              <radialGradient id="beam-glow">
                 <stop offset="0%" stop-color="rgba(255,255,255,0.4)" />
                 <stop offset="100%" stop-color="rgba(255,255,255,0)" />
              </radialGradient>
           </defs>
           <rect width="500" height="400" fill="url(#lab-bg)" />

           <!-- 介质 -->
           <rect y="200" width="500" height="200" :fill="mediumColor" opacity="0.3" stroke="rgba(255,255,255,0.1)" />
           <line x1="0" y1="200" x2="500" y2="200" stroke="rgba(255,255,255,0.2)" stroke-width="1.5" />
           <line x1="250" y1="20" x2="250" y2="380" stroke="rgba(255,255,255,0.1)" stroke-dasharray="5,5" />

           <!-- 光束渲染 -->
           <!-- 入射 -->
           <line :x1="sourcePos.x" :y1="sourcePos.y" x2="250" y2="200" stroke="#fefce8" stroke-width="4" stroke-linecap="round" />
           <line :x1="sourcePos.x" :y1="sourcePos.y" x2="250" y2="200" stroke="#fbbf24" stroke-width="1.5" opacity="0.8" />
           
           <!-- 反射 -->
           <line x1="250" y1="200" :x2="reflectedEnd.x" :y2="reflectedEnd.y" stroke="#fbbf24" stroke-width="2" opacity="0.4" stroke-dasharray="6,4" />

           <!-- 折射 ( Snell's Law ) -->
           <g v-if="!isTIR">
              <line x1="250" y1="200" :x2="refractedEnd.x" :y2="refractedEnd.y" stroke="#60a5fa" stroke-width="4" />
              <line x1="250" y1="200" :x2="refractedEnd.x" :y2="refractedEnd.y" stroke="#fff" stroke-width="1" opacity="0.3" />
           </g>
           
           <!-- 全反射高光 -->
           <line v-if="isTIR" x1="250" y1="200" :x2="reflectedEnd.x" :y2="reflectedEnd.y" stroke="#ef4444" stroke-width="5" />

           <!-- 角度标记 -->
           <g transform="translate(250, 200)">
              <text :x="-30" :y="-30" fill="#fbbf24" font-size="12" font-family="monospace">θ1: {{ Math.round(incidentAngleDeg) }}°</text>
              <text v-if="!isTIR" :x="30" :y="40" fill="#60a5fa" font-size="12" font-family="monospace">θ2: {{ Math.round(refractionAngleDeg) }}°</text>
           </g>
        </svg>

        <div class="bench-hud" v-if="isTIR">
           <div class="tir-badge">DANGER: TIR ACTIVE</div>
        </div>
      </div>

      <div class="lab-controls">
         <div class="control-box neon">
            <label>光源控制 [θ1]</label>
            <el-slider v-model="sliderAngle" :min="1" :max="89" size="mini"></el-slider>
            
            <div class="divider"></div>
            
            <label>介质折射系数 [n2]</label>
            <el-slider v-model="n2" :min="1.0" :max="2.5" :step="0.01" size="mini"></el-slider>
         </div>

         <div class="lab-notes">
            <h5 class="title"><i class="el-icon-collection"></i> 物理参考</h5>
            <p><strong>斯涅尔定律：</strong>当光从光疏介质进入光密介质时，折射光线向法线靠拢（θ2 &lt; θ1）。</p>
            <p class="highlight" v-if="n2 < 1"><strong>全反射警告：</strong>光从光密向光疏入射且角度大于临界角时发生。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'OpticsRefractionViz',
  data() {
    return {
      sliderAngle: 45,
      n2: 1.5,
      mouseX: 100,
      mouseY: 100
    }
  },
  computed: {
    incidentAngleRad() { return (this.sliderAngle) * Math.PI / 180; },
    incidentAngleDeg() { return this.sliderAngle; },
    sourcePos() {
       return {
          x: 250 - 150 * Math.sin(this.incidentAngleRad),
          y: 200 - 150 * Math.cos(this.incidentAngleRad)
       };
    },
    reflectedEnd() {
       return {
          x: 250 + 150 * Math.sin(this.incidentAngleRad),
          y: 200 - 150 * Math.cos(this.incidentAngleRad)
       };
    },
    isTIR() { 
      // Simplified: if n2 < 1.0 (relative), we might have TIR.
      // But in this demo, we assume air(1) -> medium(n2).
      return false; 
    },
    refractionAngleDeg() {
       const sinT2 = (1.0 / this.n2) * Math.sin(this.incidentAngleRad);
       return Math.asin(Math.min(0.999, sinT2)) * 180 / Math.PI;
    },
    refractedEnd() {
       const angleRad = this.refractionAngleDeg * Math.PI / 180;
       return {
          x: 250 + 150 * Math.sin(angleRad),
          y: 200 + 150 * Math.cos(angleRad)
       };
    },
    mediumColor() {
       return `rgba(96, 165, 250, 0.2)`;
    }
  },
  methods: {
    handleMouseMove(e) {
      if (e.buttons === 1) { // Only change on drag
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        if (y < 200) {
           const dx = 250 - (e.clientX - rect.left);
           const dy = 200 - y;
           const angle = Math.atan2(dx, dy) * 180 / Math.PI;
           this.sliderAngle = Math.max(1, Math.min(89, Math.round(angle)));
        }
      }
    },
    onMouseMove(e) { /* Placeholder to satisfy old template ref */ }
  }
}
</script>

<style scoped lang="scss">
.optics-lab-viz {
  flex: 1; display: flex; flex-direction: column; background: #020617; color: #f8fafc; overflow: hidden;
  border-radius: 12px; border: 1px solid #1e293b;
}

.lab-toolbar {
  padding: 12px 20px; display: flex; justify-content: space-between; align-items: center;
  background: #0f172a; border-bottom: 2px solid #334155;
  .brand { font-weight: 900; color: #38bdf8; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 1px; }
  .tag-group { display: flex; gap: 8px;
     .chip { padding: 4px 10px; background: #1e293b; border-radius: 4px; font-size: 0.65rem; font-weight: bold; border: 1px solid #334155;
        &.yellow { border-color: #fbbf24; color: #fbbf24; }
     }
  }
}

.viz-row { flex: 1; display: flex; min-height: 0; }

.optics-bench {
  flex: 1.5; position: relative; border-right: 1px solid #1e293b; cursor: crosshair;
  .bench-hud {
     position: absolute; bottom: 20px; left: 20px; pointer-events: none;
     .tir-badge { padding: 6px 12px; background: rgba(239, 68, 68, 0.2); color: #ef4444; border: 1px solid #ef4444; border-radius: 6px; font-weight: 800; font-size: 0.75rem; animation: pulse 2s infinite; }
  }
}

.lab-controls {
  flex: 0.8; display: flex; flex-direction: column; padding: 20px; gap: 20px; background: #020617;
  .control-box {
     padding: 15px; border-radius: 8px; background: #0f172a; border: 1px solid #334155;
     label { font-size: 11px; color: #94a3b8; font-weight: 800; display: block; margin-bottom: 10px; }
     .divider { height: 1px; background: #1e293b; margin: 15px 0; }
  }
  .lab-notes {
     font-size: 0.75rem; line-height: 1.6; color: #64748b;
     .title { color: #f8fafc; margin-bottom: 8px; font-size: 0.85rem; }
     .highlight { color: #fbbf24; }
  }
}

@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

::v-deep .el-slider__runway { background-color: #1e293b; height: 4px; }
::v-deep .el-slider__bar { background-color: #38bdf8; height: 4px; }
::v-deep .el-slider__button { width: 12px; height: 12px; border: 2px solid #38bdf8; background: #0f172a; }
</style>
